import { BLOCKS } from './blocks';

// Duplicated from world.ts to avoid circular imports
const CHUNK_SIZE = 16;
const CHUNK_HEIGHT = 512;

// Precomputed brightness curve — vertex colors ARE the final lighting
// Ambient light is ~1.0, so this curve directly controls visible brightness
// level 15 → 1.0, level 10 → 0.49, level 5 → 0.16, level 0 → 0.02
export const LIGHT_MULTIPLIER = new Float32Array(16);
for (let i = 0; i < 16; i++) {
  LIGHT_MULTIPLIER[i] = 0.02 + 0.98 * Math.pow(i / 15, 1.8);
}

const CHUNK_SLICE = CHUNK_SIZE * CHUNK_SIZE; // 256

// Pre-built transparency lookup (avoids BLOCKS hash lookup in hot loop)
const transparencyCache = new Uint8Array(256); // 0=opaque, 1=transparent/air
const waterCache = new Uint8Array(256);
let cacheBuilt = false;

function ensureCache() {
  if (cacheBuilt) return;
  cacheBuilt = true;
  transparencyCache[0] = 1; // air
  for (let i = 1; i < 256; i++) {
    const def = BLOCKS[i];
    transparencyCache[i] = (def && def.transparent) ? 1 : 0;
    waterCache[i] = (i === 7) ? 1 : 0;
  }
}

interface ChunkLike {
  blocks: Uint8Array;
  lightData: Uint8Array;
  maxY: number;
  cx: number;
  cz: number;
  dirty: boolean;
}

type NeighborGetter = (cx: number, cz: number) => ChunkLike | undefined;

// Direction offsets for 6-neighbor BFS (dx, dy, dz)
const DX = [1, -1, 0, 0, 0, 0];
const DY = [0, 0, 1, -1, 0, 0];
const DZ = [0, 0, 0, 0, 1, -1];

export function computeChunkLight(chunk: ChunkLike, getNeighborChunk: NeighborGetter): void {
  ensureCache();

  const { blocks, lightData } = chunk;
  const maxY = Math.min(chunk.maxY + 1, CHUNK_HEIGHT - 1);

  // Clear light data
  lightData.fill(0);

  // --- Sky light: column scan + boundary seed ---
  // Track the "floor" y for each column (first opaque block from top)
  // Only seed BFS from sky-lit positions that have a non-sky neighbor (boundary)
  const skyFloor = new Int32Array(CHUNK_SLICE); // y of first opaque block per column
  skyFloor.fill(-1);

  for (let x = 0; x < CHUNK_SIZE; x++) {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      const colBase = x + z * CHUNK_SIZE;
      for (let y = maxY; y >= 0; y--) {
        const idx = colBase + y * CHUNK_SLICE;
        if (transparencyCache[blocks[idx]]) {
          // Set sky = 15
          lightData[idx] = (15 << 4) | (lightData[idx] & 0xf);
        } else {
          skyFloor[colBase] = y;
          break;
        }
      }
    }
  }

  // BFS queue — flat array [x, y, z, level] packed as single int32 for speed
  // Use a typed circular buffer approach with a simple array + index
  const skyQueue: number[] = [];

  // Seed: only sky=15 positions that are adjacent to a non-sky-15 neighbor
  // This avoids seeding the entire open-air column
  for (let x = 0; x < CHUNK_SIZE; x++) {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      const floor = skyFloor[x + z * CHUNK_SIZE];
      if (floor < 0) continue; // entire column is sky — no boundary to seed from

      // Seed the block just above the floor (it's sky=15 and touches opaque below)
      const seedY = floor + 1;
      if (seedY <= maxY) {
        skyQueue.push(x, seedY, z, 15);
      }

      // Also seed any sky=15 position next to a column that has a lower floor
      // (handles overhangs: light spills sideways under ledges)
      for (let d = 0; d < 4; d++) {
        const nx = x + DX[d]; // only horizontal dirs (0-3 are ±x, skip y dirs)
        const nz = z + DZ[d];
        if (nx < 0 || nx >= CHUNK_SIZE || nz < 0 || nz >= CHUNK_SIZE) {
          // Chunk boundary — seed the whole exposed column edge
          for (let y = floor + 1; y <= maxY; y++) {
            skyQueue.push(x, y, z, 15);
          }
          break; // only need to seed once per column
        }
        const neighborFloor = skyFloor[nx + nz * CHUNK_SIZE];
        if (neighborFloor > floor) {
          // Neighbor column is taller — seed our exposed range so light flows into their overhang
          for (let y = floor + 1; y <= Math.min(neighborFloor, maxY); y++) {
            skyQueue.push(x, y, z, 15);
          }
        }
      }
    }
  }

  // BFS spread sky light
  let qi = 0;
  while (qi < skyQueue.length) {
    const sx = skyQueue[qi++];
    const sy = skyQueue[qi++];
    const sz = skyQueue[qi++];
    const level = skyQueue[qi++];

    if (level <= 1) continue;

    for (let d = 0; d < 6; d++) {
      const nx = sx + DX[d];
      const ny = sy + DY[d];
      const nz = sz + DZ[d];

      if (ny < 0 || ny >= CHUNK_HEIGHT) continue;

      // Cross-chunk boundary
      if (nx < 0 || nx >= CHUNK_SIZE || nz < 0 || nz >= CHUNK_SIZE) {
        const neighborCx = chunk.cx + (nx < 0 ? -1 : nx >= CHUNK_SIZE ? 1 : 0);
        const neighborCz = chunk.cz + (nz < 0 ? -1 : nz >= CHUNK_SIZE ? 1 : 0);
        const neighbor = getNeighborChunk(neighborCx, neighborCz);
        if (neighbor) {
          const lnx = ((nx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
          const lnz = ((nz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
          const nIdx = lnx + lnz * CHUNK_SIZE + ny * CHUNK_SLICE;
          const nBlockId = neighbor.blocks[nIdx];
          if (transparencyCache[nBlockId]) {
            const attenuation = waterCache[nBlockId] ? 2 : 1;
            const attenuatedLevel = level - attenuation;
            if (attenuatedLevel > 0 && ((neighbor.lightData[nIdx] >> 4) & 0xf) < attenuatedLevel) {
              neighbor.lightData[nIdx] = (attenuatedLevel << 4) | (neighbor.lightData[nIdx] & 0xf);
              neighbor.dirty = true;
            }
          }
        }
        continue;
      }

      const nIdx = nx + nz * CHUNK_SIZE + ny * CHUNK_SLICE;
      const nBlockId = blocks[nIdx];
      if (!transparencyCache[nBlockId]) continue;

      const attenuation = waterCache[nBlockId] ? 2 : 1;
      const attenuatedLevel = level - attenuation;
      if (attenuatedLevel > 0 && ((lightData[nIdx] >> 4) & 0xf) < attenuatedLevel) {
        lightData[nIdx] = (attenuatedLevel << 4) | (lightData[nIdx] & 0xf);
        skyQueue.push(nx, ny, nz, attenuatedLevel);
      }
    }
  }

  // --- Block light: BFS from emitters ---
  const blockQueue: number[] = [];

  // Scan for emitting blocks
  for (let y = 0; y <= maxY; y++) {
    const yOff = y * CHUNK_SLICE;
    for (let x = 0; x < CHUNK_SIZE; x++) {
      for (let z = 0; z < CHUNK_SIZE; z++) {
        const idx = x + z * CHUNK_SIZE + yOff;
        const blockId = blocks[idx];
        if (blockId === 0) continue;
        const def = BLOCKS[blockId];
        if (def && def.lightLevel && def.lightLevel > 0) {
          const current = lightData[idx] & 0xf;
          if (def.lightLevel > current) {
            lightData[idx] = (lightData[idx] & 0xf0) | (def.lightLevel & 0xf);
            blockQueue.push(x, y, z, def.lightLevel);
          }
        }
      }
    }
  }

  // BFS spread block light
  qi = 0;
  while (qi < blockQueue.length) {
    const bx = blockQueue[qi++];
    const by = blockQueue[qi++];
    const bz = blockQueue[qi++];
    const level = blockQueue[qi++];

    if (level <= 1) continue;

    for (let d = 0; d < 6; d++) {
      const nx = bx + DX[d];
      const ny = by + DY[d];
      const nz = bz + DZ[d];

      if (ny < 0 || ny >= CHUNK_HEIGHT) continue;

      // Cross-chunk boundary
      if (nx < 0 || nx >= CHUNK_SIZE || nz < 0 || nz >= CHUNK_SIZE) {
        const neighborCx = chunk.cx + (nx < 0 ? -1 : nx >= CHUNK_SIZE ? 1 : 0);
        const neighborCz = chunk.cz + (nz < 0 ? -1 : nz >= CHUNK_SIZE ? 1 : 0);
        const neighbor = getNeighborChunk(neighborCx, neighborCz);
        if (neighbor) {
          const lnx = ((nx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
          const lnz = ((nz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
          const nIdx = lnx + lnz * CHUNK_SIZE + ny * CHUNK_SLICE;
          const nBlockId = neighbor.blocks[nIdx];
          if (transparencyCache[nBlockId]) {
            const attenuation = waterCache[nBlockId] ? 2 : 1;
            const attenuatedLevel = level - attenuation;
            if (attenuatedLevel > 0 && (neighbor.lightData[nIdx] & 0xf) < attenuatedLevel) {
              neighbor.lightData[nIdx] = (neighbor.lightData[nIdx] & 0xf0) | (attenuatedLevel & 0xf);
              neighbor.dirty = true;
            }
          }
        }
        continue;
      }

      const nIdx = nx + nz * CHUNK_SIZE + ny * CHUNK_SLICE;
      const nBlockId = blocks[nIdx];
      if (!transparencyCache[nBlockId]) continue;

      const attenuation = waterCache[nBlockId] ? 2 : 1;
      const attenuatedLevel = level - attenuation;
      if (attenuatedLevel > 0 && (lightData[nIdx] & 0xf) < attenuatedLevel) {
        lightData[nIdx] = (lightData[nIdx] & 0xf0) | (attenuatedLevel & 0xf);
        blockQueue.push(nx, ny, nz, attenuatedLevel);
      }
    }
  }
}
